import { useEffect } from 'react'
import {
  parseTemplateFromSiteplus,
  createSiteplusFromTemplate,
  buildPagesFromCheckedSections,
} from '@/helpers/siteplusBuilder'
import { useNavigate } from 'react-router-dom'
import { useTemplateContext } from '@/context/TemplateProvider'
import { useState } from 'react'
import { useModal } from '../context/ModalContext'
import { deleteCookie, getCookieValue, setCookie } from '../helpers'
import {
  useCreateSavedState,
  useGetSavedState,
  useUpdateSavedState,
} from './useOnboarding'
import { ALL_STEPS, ONBOARDING_STATUS } from '../constants/onboarding'
import { getBrandId } from '../helpers/brandHelper'
import { clearOnboardingStateSessionId, setOnboardingStateSessionId as setSessionIdInSessionStorage } from '../helpers/onboardingSessionHelper'
import isEmpty from 'lodash/isEmpty'
import { getDomaincomDashboardPrefix, getWebcomDashboardPrefix, getNetsolDashboardPrefix } from '../helpers/envHelper'
import { useQueryClient } from '@tanstack/react-query'
import { getAbandonedDraftId } from '../api/onboarding'
import { writeGridOnboardingSession } from '@/helpers/gridOnboardingSessionHelper'

const ONBOARDING_STATE_SESSION_ID_KEY = 'onboarding_state_session_id'
const ONBOARDING_SHOW_DIALOG_KEY = 'onboarding_show_continue_dialog'

/** Pages step uses these ids; they align with `RECOMMENDED_APPS` ids for store onboarding `appChoices`. */
const GRID_APP_SECTION_IDS = new Set(['store', 'appointments', 'blog'])

/**
 * Grid: use Pages step checkboxes (`checkedSortedSections` / `sections`).
 * Non-grid: use `recommendedApps[].enabled` from the Recommended Apps step.
 */
const buildAppChoices = ({
  isGrid,
  checkedSortedSections,
  sections,
  recommendedApps,
}) => {
  const apps = recommendedApps ?? []
  const enabledForId = (appId) => {
    if (isGrid === true && GRID_APP_SECTION_IDS.has(appId)) {
      const row = checkedSortedSections?.find((s) => s.id === appId)
      if (row != null && typeof row.checked === 'boolean') {
        return row.checked
      }
      if (sections != null && Object.prototype.hasOwnProperty.call(sections, appId)) {
        return Boolean(sections[appId])
      }
    }
    const app = apps.find((a) => a.id === appId)
    return Boolean(app?.enabled)
  }

  return apps.reduce((acc, app) => {
    acc[app.id] = enabledForId(app.id)
    return acc
  }, {})
}

const useOnboardingProgress = ({ ...rest }) => {
  const { template } = useTemplateContext()
  const [onboardingStateSessionId, setonboardingStateSessionId] = useState(null)
  const [abandonedDraftId, setAbandonedDraftId] = useState(null)
  const productInstanceId = getCookieValue("_sp_productInstanceId")

  const showStateModal = getCookieValue(ONBOARDING_SHOW_DIALOG_KEY) === 'true'
  const { showModal } = useModal()

  const { data, isFetched, isError } = useGetSavedState(abandonedDraftId)
  let { aiToneTexts} = data || {}
  if (!isEmpty(aiToneTexts)) {
    try {
      aiToneTexts = JSON.parse(aiToneTexts)
    } catch (error) {
      console.error(`Error parsing aiToneTexts. aiTones: ${aiToneTexts}`, error)
      aiToneTexts = []
    }
  }
  const { mutateAsync: createOnboardingProgress } = useCreateSavedState()
  const { mutateAsync: updateOnboardingProgress } = useUpdateSavedState()

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const clearOnboardingSessionId = () => {
    clearOnboardingStateSessionId()
    deleteCookie(ONBOARDING_STATE_SESSION_ID_KEY)
    setonboardingStateSessionId(null)
  }

  useEffect(() => {
    const fetchAbandonedDraftId = async () => {
      try {
        const draftId = await getAbandonedDraftId(productInstanceId)
        setAbandonedDraftId(draftId)
        setonboardingStateSessionId(draftId)
      } catch (error) {
        console.error('Error fetching abandonedDraftId:', error)
        setAbandonedDraftId(null)
      }
    }

    fetchAbandonedDraftId()
  }, [])

  const recreateState = (recreatedState) => {
    rest.setTemplateCategoryData(recreatedState.templateCategoryData)
    rest.setTemplateStyle(recreatedState.templateStyle)
    rest.websiteDetails.set(recreatedState.websiteDetails)
    rest.setSections(recreatedState.sectionsState)
    rest.setSelectedTemplate(recreatedState.selectedTemplate)
    rest.setContentStructureData(recreatedState.contentStructureData)
    rest.setTonesData(aiToneTexts)
    rest.setRawTonesData(aiToneTexts)
    rest.setTone(recreatedState.selectedTone)

    if (recreatedState.coverImage) {
      rest.coverImage.set(recreatedState.coverImage)
    }
    
    if (recreatedState.logo) {
      rest.logoImage.set(recreatedState.logo)
    }  

    if (recreatedState.selectedPalette.title)
      rest.setSelectedPalette(recreatedState.selectedPalette)

    const font = recreatedState.selectedFont
    rest.setSelectedFont(font)

    if (font.custom) {
      rest.setCustomFont(font)
    }

    rest.setDefaultTemplatesVersion(recreatedState.templatesVersion ?? "v1")
    
    if(recreatedState.selectedApps) {
      rest.setRecommendedApps(recreatedState.selectedApps)
    }
    
    if(recreatedState.appsSetupData) {
      rest.setAppsSetupData(recreatedState.appsSetupData)
    }

    if (recreatedState.gridSitewizardId != null) {
      rest.setGridSitewizardId(recreatedState.gridSitewizardId)
    }
    if (recreatedState.gridAiPageData != null) {
      rest.setGridAiPageData(recreatedState.gridAiPageData)
    }
    if (recreatedState.lastGridGeneratedTemplateId != null) {
      rest.setLastGridGeneratedTemplateId(recreatedState.lastGridGeneratedTemplateId)
    }
    if (
      recreatedState.gridAiPageData &&
      typeof recreatedState.gridAiPageData === 'object' &&
      Object.keys(recreatedState.gridAiPageData).length > 0
    ) {
      rest.setGridGenerationStatus('ready')
    }

    if (
      rest.isGrid === true &&
      recreatedState.gridSitewizardId &&
      rest.productInstanceId
    ) {
      writeGridOnboardingSession(rest.productInstanceId, {
        sitewizardId: recreatedState.gridSitewizardId,
        aiPageData: recreatedState.gridAiPageData ?? {},
        lastGeneratedTemplateId: recreatedState.lastGridGeneratedTemplateId,
      })
    }
  }

  const navigateToForm = ({ step = 0, subStep = 0 } = {}) => {
    rest.setActualStep(step)
    rest.setActualSubStep(subStep)
    navigate('form')
  }

  const handleGet = async (data) => {
    deleteCookie(ONBOARDING_STATE_SESSION_ID_KEY)
    deleteCookie(ONBOARDING_SHOW_DIALOG_KEY)

    const { onboardingState: onboardingProgress } = data

    if (!onboardingProgress) {
      setonboardingStateSessionId(null)
      return
    }

    onboardingProgress.onboardingJson = JSON.parse(
      onboardingProgress.onboardingJson
    )

    const recreatedState = parseTemplateFromSiteplus(onboardingProgress, rest)

    recreateState(recreatedState)
    navigateToForm(recreatedState)

    if (!showStateModal) return

    const continueEditing = await showModal({
      type: 'state',
      resolutionType: 'confirm',
    })

    if (!continueEditing) {
      clearOnboardingStateSessionId()
      setCookie("onboarding_no_redirect", true)

      const host = window.location.hostname
      const baseDomain = host.includes('.')
        ? `${host.split('.').slice(-2).join('.')}`
        : host

      switch (baseDomain) {
        case 'domain.com':
          window.location.href = `${getDomaincomDashboardPrefix()}.${baseDomain}/op/website`
          return
        case 'web.com':
          window.location.href = `${getWebcomDashboardPrefix()}.${baseDomain}/op/website`
          return
        case 'networksolutions.com':
        case 'netsol.com':
          window.location.href = `${getNetsolDashboardPrefix()}.${baseDomain}/op/website`
          return
        default:
          window.location.href = `${getWebcomDashboardPrefix()}.${baseDomain}/op/website`
      }
    }
  }

  const saveState = async (markAsComplete = false) => {
    if(!rest.websiteDetails.data?.title) return

    const currentState = createSiteplusFromTemplate({
      templateCategoryData: rest.templateCategoryData,
      websiteDetails: rest.websiteDetails,
      selectedFont: rest.selectedFont ?? {},
      contentStructureData: rest.contentStructureData,
      selectedTemplate: rest.selectedTemplate,
      template: template,
      selectedPalette: rest.selectedPalette ?? {},
      productInstanceId: rest.productInstanceId,
      sections: rest.sections ?? {},
      accessToken: rest.accessToken,
      layout: rest.layout,
      isGrid: rest.isGrid,
      recommendedApps: rest.recommendedApps,
      selectedTone: rest.selectedTone,
      pages: buildPagesFromCheckedSections(rest.checkedSortedSections),
    })

    //Remove extra fields
    delete currentState.accessToken
    delete currentState.productInstanceId
    delete currentState.isTrial
    delete currentState.type

    if (rest.selectedPalette) {
      currentState.theme.title = rest.selectedPalette.title
    }

    if (rest.coverImage.data) {
      currentState.coverImageId = rest.coverImage.data.id
    }

    currentState.unique = rest.websiteDetails?.data?.title
    currentState.step = rest.actualStep
    currentState.subStep = rest.actualSubStep
    currentState.photoCategory = rest.templateCategoryData.photoCategory
    currentState.templateStyle = rest.templateStyle
    currentState.sections = rest.sections
    currentState.typography.isCustom = rest.selectedFont?.custom
    currentState.templatesVersion = rest.defaultTemplatesVersion
    currentState.appsSetupData = rest.appsSetupData
    currentState.selectedTone = rest.selectedTone

    if (rest.isGrid === true) {
      if (rest.gridSitewizardId != null) {
        currentState.gridSitewizardId = rest.gridSitewizardId
      }
      if (rest.gridAiPageData != null) {
        currentState.gridAiPageData = rest.gridAiPageData
      }
      if (rest.lastGridGeneratedTemplateId != null) {
        currentState.lastGridGeneratedTemplateId = rest.lastGridGeneratedTemplateId
      }
    }

    const choices = buildAppChoices({
      isGrid: rest.isGrid,
      checkedSortedSections: rest.checkedSortedSections,
      sections: rest.sections,
      recommendedApps: rest.recommendedApps,
    })

    currentState.selectedApps = (rest.recommendedApps ?? []).map((app) => ({
      ...app,
      enabled: choices[app.id] ?? app.enabled,
    }))

    let payload = {
      productInstanceId: rest.productInstanceId,
      title: rest.websiteDetails?.data?.title,
      currentStep: rest.stepSequence + 1,
      onboardingJson: currentState,
      onboardingStatus: ONBOARDING_STATUS.InProgress,
      partnerType: getBrandId(),
      templateId: currentState.templateId,
      appChoices: JSON.stringify(choices),
      phoneNumber: rest.appsSetupData?.phoneNumber?.replace(/[^0-9]/g, "") || null,
    }

    const appsSetupData = rest.appsSetupData
    if(!isEmpty(appsSetupData) && (payload.currentStep == ALL_STEPS.length || markAsComplete)) {
      payload = {
        ...payload,
        businessAddressPlaceId: appsSetupData.businessAddress?.Place_Id || null,
        businessAddressStreet2: appsSetupData.businessAptSuite || null,
        businessAddressZipcode: appsSetupData.businessPostalCode || null,
        businessAddressStreet1: appsSetupData.businessAddress?.Place_Id? null : appsSetupData.businessAddress_input || null
      }

      if(appsSetupData?.sameShippingAddress) {
        payload = {
          ...payload,
          shippingAddressPlaceId: payload.businessAddressPlaceId,
          shippingAddressStreet2: payload.businessAddressStreet2,
          shippingAddressZipcode: payload.businessAddressZipcode,
        }
      } else {
        payload = {
          ...payload,
          shippingAddressPlaceId: appsSetupData.shippingAddress?.Place_Id || null,
          shippingAddressStreet2: appsSetupData.shippingAptSuite || null,
          shippingAddressZipcode: appsSetupData.shippingPostalCode || null,
        }
      }

      payload = {
        ...payload,
        sessionToken: onboardingStateSessionId
      }    
    }

    if (!onboardingStateSessionId) {
      const createResponse = await createOnboardingProgress(payload)
      setonboardingStateSessionId(createResponse.onboardingSessionId)
    } else {
      await updateOnboardingProgress({
        ...payload,
        onboardingSessionId: onboardingStateSessionId,
        onboardingStatus: payload.currentStep == ALL_STEPS.length || markAsComplete? ONBOARDING_STATUS.Complete: ONBOARDING_STATUS.InProgress,
      })
    }

    if (markAsComplete || payload.currentStep == ALL_STEPS.length) {
      clearOnboardingStateSessionId()
      queryClient.invalidateQueries("STOREDASH_QUERY")
    }
  }

  useEffect(() => {
    if (rest.stepSequence == null && !onboardingStateSessionId) return
    saveState()
  }, [rest.stepSequence])

  useEffect(() => {
    if (isError) {
      setonboardingStateSessionId(null)
      return
    }
    if (isFetched) {
      handleGet({ ...data })
    }
  }, [isFetched, data, isError])

  useEffect(() => {
    if (onboardingStateSessionId) {
        setSessionIdInSessionStorage(onboardingStateSessionId)
    }
  }, [onboardingStateSessionId])

  const markAsComplete = async () => saveState(true) 

  return {
    markAsComplete,
    onboardingStateSessionId,
    clearOnboardingSessionId
  }
}

export default useOnboardingProgress
